import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { MembersService } from '@/lib/api/services/members'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

dayjs.extend(isSameOrAfter)

// 通知取得用の非同期thunk
export const getNotifications = createAsyncThunk(
  'notification/getNotifications',
  async (memberId: number, { rejectWithValue }) => {
    try {
      const response = await MembersService.getMemberNotifications(memberId.toString())
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '通知の取得に失敗しました')
    }
  },
)

export interface NotificationData {
  text: string
  date?: string
  linkTo?: string
}

export interface NotificationValue {
  title: string
  data: NotificationData[]
  readAt: string | null
  confirmed: boolean
}

export interface ApiNotificationResponse {
  values: {
    notices: NotificationValue
    info: NotificationValue
  }
}

export interface NotificationItem {
  id: number
  title: string
  content: string
  type: 'notices' | 'info'
  isRead: boolean
  createdAt: string
  url?: string
}

interface NotificationState {
  notifications: NotificationItem[]
  noticesData: NotificationValue | null
  infoData: NotificationValue | null
  unreadCount: number
  activeTab: 'notices' | 'info'
  tabSetByUser: boolean
  allConfirmed: boolean
  isLoading: boolean
  error: string | null
}

const initialState: NotificationState = {
  notifications: [],
  noticesData: null,
  infoData: null,
  unreadCount: 0,
  activeTab: 'notices',
  tabSetByUser: false,
  allConfirmed: true,
  isLoading: false,
  error: null,
}

export const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<ApiNotificationResponse>) => {
      const { values } = action.payload

      // タイトルを設定
      values.notices.title = 'お知らせ'
      values.info.title = '通知'

      // 確認状態を初期化
      values.notices.confirmed = true
      values.info.confirmed = true

      // 更新通知の確認状態をチェック（直近1ヶ月以内のもののみ対象）
      const oneMonthAgo = dayjs().subtract(1, 'month')
      values.notices.data.forEach((item) => {
        if (!item.date || !dayjs(item.date).isAfter(oneMonthAgo)) return
        if (!values.notices.readAt || dayjs(item.date).isAfter(values.notices.readAt)) {
          values.notices.confirmed = false
        }
      })

      // 通知の確認状態をチェック
      values.info.data.forEach((item) => {
        if (!values.info.readAt || (item.date && dayjs(item.date).isAfter(values.info.readAt))) {
          values.info.confirmed = false
        }
      })

      // 全体の確認状態を設定
      state.allConfirmed = values.notices.confirmed && values.info.confirmed

      // 空データの処理（activeTabの決定はInfoListPageClientに委譲）
      if (!values.notices.data.length) {
        values.notices.data.push({ text: '最新のお知らせはありません' })
      }
      if (!values.info.data.length) {
        values.info.data.push({ text: '最新の通知はありません' })
      }

      state.noticesData = values.notices
      state.infoData = values.info
      state.error = null
    },
    markAsRead: (state, action: PayloadAction<number>) => {
      const notification = state.notifications.find((item) => item.id === action.payload)
      if (notification && !notification.isRead) {
        notification.isRead = true
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    },
    markAllAsRead: (state, action: PayloadAction<'notices' | 'info'>) => {
      const type = action.payload

      if (type === 'notices' && state.noticesData) {
        state.noticesData.confirmed = true
      } else if (type === 'info' && state.infoData) {
        state.infoData.confirmed = true
      }

      // 全体の確認状態を更新
      state.allConfirmed =
        (!state.noticesData || state.noticesData.confirmed) &&
        (!state.infoData || state.infoData.confirmed)
    },
    setActiveTab: (state, action: PayloadAction<'notices' | 'info'>) => {
      state.activeTab = action.payload
      state.tabSetByUser = true
    },
    setActiveTabAuto: (state, action: PayloadAction<'notices' | 'info'>) => {
      state.activeTab = action.payload
    },
    resetTabSetByUser: (state) => {
      state.tabSetByUser = false
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.isLoading = false
        const { values } = action.payload

        // タイトル設定
        values.notices.title = 'お知らせ'
        values.info.title = '通知'

        // 確認状態を初期化
        values.notices.confirmed = true
        values.info.confirmed = true

        // 更新通知の確認状態をチェック（直近1ヶ月以内のもののみ対象）
        const oneMonthAgo = dayjs().subtract(1, 'month')
        values.notices.data.forEach((item: NotificationData) => {
          if (!item.date || !dayjs(item.date).isAfter(oneMonthAgo)) return
          if (!values.notices.readAt || dayjs(item.date).isAfter(values.notices.readAt)) {
            values.notices.confirmed = false
          }
        })

        // 通知の確認状態をチェック
        values.info.data.forEach((item: NotificationData) => {
          if (!values.info.readAt || (item.date && dayjs(item.date).isAfter(values.info.readAt))) {
            values.info.confirmed = false
          }
        })

        // 全体の確認状態を設定
        state.allConfirmed = values.notices.confirmed && values.info.confirmed

        // 空データの処理（activeTabの決定はInfoListPageClientに委譲）
        if (!values.notices.data.length) {
          values.notices.data.push({ text: '最新のお知らせはありません' })
        }
        if (!values.info.data.length) {
          values.info.data.push({ text: '最新の通知はありません' })
        }

        state.noticesData = values.notices
        state.infoData = values.info
        state.error = null
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '通知の取得に失敗しました'
      })
  },
})

export const {
  setNotifications,
  markAsRead,
  markAllAsRead,
  setActiveTab,
  setActiveTabAuto,
  resetTabSetByUser,
  setLoading,
  setError,
  clearError,
} = notificationSlice.actions

export const selectNotifications = (state: RootState): NotificationItem[] =>
  state.notification.notifications
export const selectUnreadCount = (state: RootState): number => {
  let count = 0
  if (state.notification.noticesData && !state.notification.noticesData.confirmed) {
    count++
  }
  if (state.notification.infoData && !state.notification.infoData.confirmed) {
    count++
  }
  return count
}
export const selectActiveTab = (state: RootState): 'notices' | 'info' =>
  state.notification.activeTab
export const selectTabSetByUser = (state: RootState): boolean => state.notification.tabSetByUser
export const selectNotificationLoading = (state: RootState): boolean => state.notification.isLoading
export const selectNotificationError = (state: RootState): string | null => state.notification.error

export const selectNotificationsByType = (
  state: RootState,
  type: 'notices' | 'info',
): NotificationItem[] => state.notification.notifications.filter((item) => item.type === type)

export const selectUnreadCountByType = (state: RootState, type: 'notices' | 'info'): number => {
  if (type === 'info') {
    return state.notification.infoData && !state.notification.infoData.confirmed ? 1 : 0
  } else {
    return state.notification.noticesData && !state.notification.noticesData.confirmed ? 1 : 0
  }
}

export const selectAllConfirmed = (state: RootState): boolean => state.notification.allConfirmed
export const selectNoticesData = (state: RootState): NotificationValue | null =>
  state.notification.noticesData
export const selectInfoData = (state: RootState): NotificationValue | null =>
  state.notification.infoData

export default notificationSlice.reducer
